import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  TextInput,
  PasswordInput,
  Button,
  Form,
  Stack,
  InlineNotification,
  Theme,
  Tile,
} from '@carbon/react';
import { Login as LoginIcon } from '@carbon/icons-react';
import logo from '../assets/logo.png';
import backgroundLogin from '../assets/background-login.jpg';
import ChristmasDecor from './ChristmasDecor';
import './Login.css';

/**
 * Check if current date is in Christmas period (Dec 10 - Jan 8)
 */
const isChristmasSeason = () => {
  const now = new Date();
  const month = now.getMonth() + 1; // 0-indexed, so +1
  const day = now.getDate();
  
  // December 10-31 or January 1-8
  return (month === 12 && day >= 10) || (month === 1 && day <= 8);
};

/**
 * Login Page Component
 * Handles user authentication with username and password
 */
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, hasSetHisPassword } = useAuth();
  const isChristmas = isChristmasSeason();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // If already authenticated but hasn't set password yet, redirect to set-password
  if (isAuthenticated && hasSetHisPassword === false) {
    return <Navigate to="/set-password" replace />;
  }

  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  /**
   * Handle input changes
   */
  const handleChange = (e) => {
    const { id, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Clear field error when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: '',
      }));
    }

    // Clear server error
    if (serverError) {
      setServerError('');
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username) {
      newErrors.username = 'Korisničko ime je obavezno';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Korisničko ime mora imati najmanje 3 karaktera';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Lozinka je obavezna';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Lozinka mora imati najmanje 6 karaktera';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setServerError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Call login from auth context
      const response = await login(formData.username, formData.password);

      // If user hasn't set their password yet, redirect to set-password page
      if (response?.data?.hasSetHisPassword === false) {
        navigate('/set-password', { replace: true });
        return;
      }

      // Get the page they were trying to access or default to dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      
      // Redirect to the intended page
      navigate(from, { replace: true });
    } catch (error) {
      // Display error message
      setServerError(error.message || 'Prijavljivanje nije uspelo. Pokušajte ponovo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Theme theme="white">
      {isChristmas && <ChristmasDecor />}
      <div className="login-container">
        {/* Background Image with white overlay */}
        <div
          className="login-background"
          style={{ backgroundImage: `url(${backgroundLogin})` }}
          aria-hidden="true"
        />
        <div className="login-background-overlay" aria-hidden="true" />

        <div className="login-content">
          {/* Logo */}
          <div className="login-logo-container">
            <img src={logo} alt="Logo" className="login-logo" />
          </div>

          {/* Slogan */}
          <p className="login-slogan">Ostanite povezani.</p>

          {/* Login Card */}
          <Tile className="login-tile">
            {/* Header */}
            <div className="login-header">
              <h1 className="login-title">Prijava</h1>
              <p className="login-subtitle">
                {isChristmas ? 'Srećne praznike! Pristupite svom nalogu' : 'Pristupite svom nalogu'}
              </p>
            </div>

            {/* Server Error Message */}
            {serverError && (
              <div style={{ 
                position: 'fixed', 
                top: '4rem', 
                right: '1rem', 
                zIndex: 9999,
                minWidth: '320px',
                animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <style>
                  {`
                    @keyframes slideInFromRight {
                      from {
                        transform: translateX(calc(100% + 1rem));
                        opacity: 0;
                      }
                      to {
                        transform: translateX(0);
                        opacity: 1;
                      }
                    }
                  `}
                </style>
                <InlineNotification
                  kind="error"
                  title="Greška"
                  subtitle={serverError}
                  lowContrast
                  hideCloseButton={false}
                  onCloseButtonClick={() => setServerError('')}
                />
              </div>
            )}

            {/* Login Form */}
            <Form onSubmit={handleSubmit} className="login-form">
              <Stack gap={5}>
                {/* Username Field */}
                <TextInput
                  id="username"
                  labelText="Korisničko ime"
                  placeholder="Unesite korisničko ime"
                  value={formData.username}
                  onChange={handleChange}
                  invalid={!!errors.username}
                  invalidText={errors.username}
                  disabled={isLoading}
                  autoComplete="username"
                  size="lg"
                />

                {/* Password Field */}
                <PasswordInput
                  id="password"
                  labelText="Lozinka"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  invalid={!!errors.password}
                  invalidText={errors.password}
                  disabled={isLoading}
                  autoComplete="current-password"
                  size="lg"
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                  className="login-button"
                >
                  {isLoading ? 'Prijavljivanje...' : 'Prijavi se'}
                </Button>
              </Stack>
            </Form>
          </Tile>
        </div>
      </div>
    </Theme>
  );
};

export default Login;
